/**
 * PersonFormDialog - Formulário de criação/edição de Pessoa
 *
 * Ao digitar um CPF/CNPJ já cadastrado, avisa e oferece acrescentar o papel
 * ao cadastro existente em vez de duplicar (BR-PES-001). Se o documento
 * existir em Clientes, oferece apenas a ligação opcional (BR-PES-003).
 *
 * RELEASE: 2026-10-06 v2.0.0 - Mesmo leiaute e validações do modal de Cliente
 *          (CustomerFormDialog): cabeçalho fixo, corpo rolável, rodapé fixo, seções
 *          FormSection, abas PF/PJ, máscaras e validação de CPF/CNPJ, PhoneInput,
 *          busca de CEP (ViaCEP) e título conforme o tipo da página.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 1 do módulo Estoque).
 */
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { Building2, Briefcase, Layers, Loader2, Mail, MapPin, Search, StickyNote, User, UserPlus, Tags } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhoneInput } from '@/components/ui/phone-input';
import { InlineNotice } from '@/components/ui/inline-notice';
import { FormSection, FieldLabel } from '@/components/common/FormSection';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { applyCPFMask, applyCNPJMask, applyCEPMask } from '@/lib/input-masks';
import { validateCPF, validateCNPJ } from '@/lib/input-validators';
import { fetchAddressByCep } from '@/lib/cep-lookup';
import {
  PERSON_ROLES,
  PERSON_ROLE_SECTIONS,
  usePersonsMutations,
  type Person,
  type PersonDocumentCheck,
  type PersonRole,
} from '@/hooks/usePersons';

/** Formata CPF/CNPJ só para exibição. */
export function formatPersonDocument(doc: string | null | undefined): string {
  const d = (doc || '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return d;
}

interface FormState {
  person_type: 'PF' | 'PJ';
  document: string;
  name: string;
  trade_name: string;
  email: string;
  phone: string;
  cep: string;
  street: string;
  address_number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  notes: string;
  roles: PersonRole[];
  service_area: string;
  state_registration: string;
  payment_term_days: string;
  linked_customer_id: string | null;
}

const empty: FormState = {
  person_type: 'PJ', document: '', name: '', trade_name: '', email: '', phone: '', cep: '', street: '',
  address_number: '', complement: '', district: '', city: '', state: '', notes: '', roles: [],
  service_area: '', state_registration: '', payment_term_days: '', linked_customer_id: null,
};

/** Validação no mesmo padrão do modal de Cliente (obrigatórios por tipo + dígito do documento). */
const personSchema = z.object({
  person_type: z.enum(['PF', 'PJ']),
  document: z.string(),
  name: z.string().max(150, 'Máximo de 150 caracteres'),
  email: z.string().max(150).refine((v) => !v || z.string().email().safeParse(v).success, 'E-mail inválido'),
  roles: z.array(z.string()).min(1, 'Selecione ao menos um papel'),
}).superRefine((d, ctx) => {
  if (!d.name.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['name'], message: d.person_type === 'PF' ? 'Nome completo é obrigatório' : 'Razão social é obrigatória' });
  }
  const digits = d.document.replace(/\D/g, '');
  if (d.person_type === 'PF') {
    if (!digits) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['document'], message: 'CPF é obrigatório para Pessoa Física' });
    else if (!validateCPF(digits)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['document'], message: 'CPF inválido' });
  } else {
    if (!digits) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['document'], message: 'CNPJ é obrigatório para Pessoa Jurídica' });
    else if (!validateCNPJ(digits)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['document'], message: 'CNPJ inválido' });
  }
});

type Errors = Partial<Record<'name' | 'document' | 'email' | 'roles', string>>;

interface Props {
  open: boolean;
  person: Person | null;
  onOpenChange: (open: boolean) => void;
  /** Abre o cadastro existente quando o documento já existe. */
  onOpenExisting: (person: Person) => void;
  /** Tipo já marcado ao criar (submenus especialistas de Pessoas). */
  defaultRole?: PersonRole | null;
  /** Informa ao componente chamador qual Pessoa foi criada ou atualizada. */
  onSuccess?: (personId: string, personName: string) => void;
}

/** Mensagem de erro abaixo do campo (mesmo visual do FormMessage). */
const FieldError = ({ msg }: { msg?: string }) => (msg ? <p className="text-[0.8rem] font-medium text-destructive">{msg}</p> : null);

export function PersonFormDialog({ open, person, onOpenChange, defaultRole, onSuccess }: Props) {
  const isMobile = useIsMobile();
  const { checkDocument, upsertPerson, addRole } = usePersonsMutations();
  const [form, setForm] = useState<FormState>(empty);
  const [check, setCheck] = useState<PersonDocumentCheck | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  // Título conforme o tipo da página (ex.: Novo fornecedor / Editar fornecedor)
  const section = PERSON_ROLE_SECTIONS.find((s) => s.role === defaultRole) ?? null;
  const entity = section ? section.label.toLowerCase().replace(' de serviço', '') : 'pessoa';
  const title = person ? `Editar ${entity}` : section?.newLabel ?? 'Nova pessoa';

  // Preenche o formulário ao abrir
  useEffect(() => {
    if (!open) return;
    setCheck(null);
    setErrors({});
    if (!person) { setForm({ ...empty, roles: defaultRole ? [defaultRole] : [] }); return; }
    const docDigits = (person.document || '').replace(/\D/g, '');
    setForm({
      person_type: person.person_type,
      document: person.person_type === 'PF' ? applyCPFMask(docDigits) : applyCNPJMask(docDigits),
      name: person.name,
      trade_name: person.trade_name ?? '', email: person.email ?? '', phone: person.phone ?? '',
      cep: person.cep ? applyCEPMask(person.cep) : '',
      street: person.street ?? '', address_number: person.address_number ?? '', complement: person.complement ?? '',
      district: person.district ?? '', city: person.city ?? '', state: person.state ?? '', notes: person.notes ?? '',
      roles: person.roles, service_area: person.provider_details?.service_area ?? '',
      state_registration: person.supplier_details?.state_registration ?? '',
      payment_term_days: person.supplier_details?.payment_term_days?.toString() ?? '',
      linked_customer_id: person.linked_customer_id,
    });
  }, [open, person, defaultRole]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  /** Troca PF/PJ limpando o documento (máscaras diferentes). */
  const handleTypeChange = (v: string) => {
    setForm((f) => ({ ...f, person_type: v as 'PF' | 'PJ', document: '' }));
    setCheck(null);
    setErrors((e) => ({ ...e, document: undefined }));
  };

  const handleDocumentChange = (value: string) => {
    set('document', form.person_type === 'PF' ? applyCPFMask(value) : applyCNPJMask(value));
    setCheck(null);
  };

  /** Ao sair do campo documento, checa duplicidade (só em novo cadastro e documento completo). */
  const handleDocumentBlur = async () => {
    if (person) return;
    const digits = form.document.replace(/\D/g, '');
    if (digits.length !== (form.person_type === 'PF' ? 11 : 14)) return;
    setCheck(await checkDocument(form.document));
  };

  /** Busca de CEP — mesma regra do modal de Cliente; foca o Número ao concluir. */
  const handleCepSearch = async () => {
    setIsLoadingCep(true);
    try {
      const addr = await fetchAddressByCep(form.cep);
      setForm((f) => ({ ...f, street: addr.street, district: addr.district, city: addr.city, state: addr.state }));
      (document.querySelector('input[name="person_address_number"]') as HTMLInputElement | null)?.focus();
      toast.success('Endereço preenchido!');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setIsLoadingCep(false);
    }
  };

  const toggleRole = (role: PersonRole, checked: boolean) =>
    set('roles', checked ? [...form.roles, role] : form.roles.filter((r) => r !== role));

  const duplicatePerson = !person && check?.person ? check.person : null;

  /** Acrescenta os papéis marcados ao cadastro já existente. */
  const handleAddRolesToExisting = async () => {
    if (!duplicatePerson) return;
    const missing = form.roles.filter((r) => !duplicatePerson.roles.includes(r));
    for (const role of missing) await addRole.mutateAsync({ personId: duplicatePerson.id, role });
    onSuccess?.(duplicatePerson.id, duplicatePerson.name);
    onOpenChange(false);
  };

  const handleSave = async () => {
    // Validação no cliente (o backend valida de novo)
    const parsed = personSchema.safeParse(form);
    if (!parsed.success) {
      const next: Errors = {};
      parsed.error.issues.forEach((i) => { const k = i.path[0] as keyof Errors; if (!next[k]) next[k] = i.message; });
      setErrors(next);
      return;
    }
    setErrors({});
    const isProvider = form.roles.includes('service_provider');
    const isSupplier = form.roles.includes('supplier');
    const savedPersonId = await upsertPerson.mutateAsync({
      id: person?.id ?? null,
      data: {
        person_type: form.person_type,
        document: form.document.replace(/\D/g, '') || null,
        name: form.name.trim(),
        legal_name: form.person_type === 'PJ' ? form.name.trim() : null,
        trade_name: form.trade_name, email: form.email.trim(), phone: form.phone,
        cep: form.cep.replace(/\D/g, ''), street: form.street,
        address_number: form.address_number, complement: form.complement, district: form.district,
        city: form.city, state: form.state, notes: form.notes,
        linked_customer_id: form.linked_customer_id,
        is_active: person?.is_active ?? true,
        roles: form.roles,
        ...(isProvider ? { provider_details: { service_area: form.service_area } } : {}),
        ...(isSupplier ? { supplier_details: { state_registration: form.state_registration, payment_term_days: form.payment_term_days || null } } : {}),
      },
    });
    onSuccess?.(savedPersonId, form.name.trim());
    onOpenChange(false);
  };

  const isPF = form.person_type === 'PF';
  const inputCls = 'h-9 border-border/50';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 overflow-hidden p-0',
          isMobile ? 'h-[100dvh] w-screen max-w-full rounded-none' : 'max-h-[90vh] max-w-5xl',
        )}
      >
        {/* Cabeçalho fixo (padrão CustomerFormDialog) */}
        <DialogHeader className="shrink-0 border-b p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5">
              {upsertPerson.isPending ? <Loader2 className="h-5 w-5 animate-spin text-primary" />
                : !person ? <UserPlus className="h-5 w-5 text-primary" />
                : isPF ? <User className="h-5 w-5 text-primary" /> : <Building2 className="h-5 w-5 text-primary" />}
            </div>
            <div className="text-left">
              <DialogTitle className="text-lg">{title}</DialogTitle>
              <DialogDescription className="text-sm">
                {person ? 'Atualize as informações abaixo.' : 'Preencha os dados. Cada CPF/CNPJ tem um único cadastro.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
            {/* ================= Tipo de pessoa ================= */}
            <FormSection icon={Layers} title="Tipo de Pessoa">
              <Tabs value={form.person_type} onValueChange={handleTypeChange} className="w-full">
                <TabsList className="grid w-full grid-cols-2 sm:max-w-md">
                  <TabsTrigger value="PF" className="gap-2"><User className="h-4 w-4" /> Pessoa Física</TabsTrigger>
                  <TabsTrigger value="PJ" className="gap-2"><Building2 className="h-4 w-4" /> Pessoa Jurídica</TabsTrigger>
                </TabsList>
              </Tabs>
            </FormSection>

            <Separator />

            {/* ================= Dados pessoais / da empresa ================= */}
            <FormSection icon={isPF ? User : Building2} title={isPF ? 'Dados Pessoais' : 'Dados da Empresa'}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1.5">
                  <FieldLabel required>{isPF ? 'Nome Completo' : 'Razão Social'}</FieldLabel>
                  <Input className={inputCls} maxLength={150} placeholder={isPF ? 'Nome completo' : 'Razão social da empresa'}
                    value={form.name} onChange={(e) => set('name', e.target.value)} />
                  <FieldError msg={errors.name} />
                </div>
                {!isPF && (
                  <div className="space-y-1.5">
                    <FieldLabel>Nome Fantasia</FieldLabel>
                    <Input className={inputCls} maxLength={150} placeholder="Nome fantasia"
                      value={form.trade_name} onChange={(e) => set('trade_name', e.target.value)} />
                  </div>
                )}
                <div className="space-y-1.5">
                  <FieldLabel required>{isPF ? 'CPF' : 'CNPJ'}</FieldLabel>
                  <Input className={inputCls} inputMode="numeric" placeholder={isPF ? '000.000.000-00' : '00.000.000/0000-00'}
                    value={form.document} onChange={(e) => handleDocumentChange(e.target.value)} onBlur={handleDocumentBlur} />
                  <FieldError msg={errors.document} />
                </div>
              </div>

              {/* Aviso de duplicidade: evita dois cadastros para o mesmo CPF/CNPJ */}
              {duplicatePerson && (
                <InlineNotice variant="warning" className="mt-3">
                  <strong>Este documento já está cadastrado.</strong>{' '}
                  {duplicatePerson.name} já existe em Pessoas. Marque os papéis abaixo e use "Acrescentar papéis" para
                  completar o cadastro existente, sem criar outro.
                </InlineNotice>
              )}
              {!person && !duplicatePerson && check?.customer && (
                <InlineNotice variant="info" className="mt-3">
                  <strong>Documento encontrado em Clientes.</strong>{' '}
                  {check.customer.name} é cliente. Você pode ligar este cadastro ao cliente; nada será alterado nele.
                  <label className="mt-2 flex items-center gap-2 text-sm">
                    <Checkbox checked={form.linked_customer_id === check.customer.id}
                      onCheckedChange={(c) => set('linked_customer_id', c ? check.customer!.id : null)} />
                    Ligar ao cliente
                  </label>
                </InlineNotice>
              )}
            </FormSection>

            <Separator />

            {/* ================= Contato ================= */}
            <FormSection icon={Mail} title="Contato" hint="(opcional)">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <FieldLabel>E-mail</FieldLabel>
                  <Input className={inputCls} type="email" maxLength={150} placeholder="email@exemplo.com"
                    value={form.email} onChange={(e) => set('email', e.target.value)} />
                  <FieldError msg={errors.email} />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Telefone</FieldLabel>
                  <PhoneInput value={form.phone} onChange={(v) => set('phone', v)} placeholder="(00) 00000-0000" />
                </div>
              </div>
            </FormSection>

            <Separator />

            {/* ================= Endereço ================= */}
            <FormSection icon={MapPin} title="Endereço" hint="(opcional)">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel>CEP</FieldLabel>
                  <div className="flex gap-2">
                    <Input className={inputCls} placeholder="00000-000" inputMode="numeric"
                      value={form.cep} onChange={(e) => set('cep', applyCEPMask(e.target.value))} />
                    <Button type="button" variant="outline" size="sm" className="h-9 shrink-0" onClick={handleCepSearch} disabled={isLoadingCep}
                      aria-label="Buscar CEP">
                      {isLoadingCep ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5 sm:col-span-3">
                  <FieldLabel>Logradouro</FieldLabel>
                  <Input className={inputCls} maxLength={150} placeholder="Rua, avenida..." value={form.street} onChange={(e) => set('street', e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-1">
                  <FieldLabel>Número</FieldLabel>
                  <Input className={inputCls} name="person_address_number" maxLength={20} placeholder="Nº" value={form.address_number} onChange={(e) => set('address_number', e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel>Complemento</FieldLabel>
                  <Input className={inputCls} maxLength={100} placeholder="Apto, sala..." value={form.complement} onChange={(e) => set('complement', e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel>Bairro</FieldLabel>
                  <Input className={inputCls} maxLength={100} placeholder="Bairro" value={form.district} onChange={(e) => set('district', e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-1">
                  <FieldLabel>Cidade</FieldLabel>
                  <Input className={inputCls} maxLength={100} placeholder="Cidade" value={form.city} onChange={(e) => set('city', e.target.value)} />
                </div>
                <div className="space-y-1.5 sm:col-span-1">
                  <FieldLabel>UF</FieldLabel>
                  <Input className={inputCls} maxLength={2} placeholder="UF" value={form.state} onChange={(e) => set('state', e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())} />
                </div>
              </div>
            </FormSection>

            <Separator />

            {/* ================= Papéis ================= */}
            <FormSection icon={Tags} title="Papéis">
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {PERSON_ROLES.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.roles.includes(r.value)} onCheckedChange={(c) => toggleRole(r.value, !!c)} />
                    {r.label}
                  </label>
                ))}
              </div>
              <FieldError msg={errors.roles} />
            </FormSection>

            {/* ================= Dados do papel ================= */}
            {(form.roles.includes('service_provider') || form.roles.includes('supplier')) && (
              <>
                <Separator />
                <FormSection icon={Briefcase} title="Dados do Papel">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {form.roles.includes('service_provider') && (
                      <div className="space-y-1.5">
                        <FieldLabel>Área de atendimento (prestador)</FieldLabel>
                        <Input className={inputCls} maxLength={200} placeholder="Ex.: Grande BH" value={form.service_area} onChange={(e) => set('service_area', e.target.value)} />
                      </div>
                    )}
                    {form.roles.includes('supplier') && (
                      <>
                        <div className="space-y-1.5">
                          <FieldLabel>Inscrição estadual</FieldLabel>
                          <Input className={inputCls} maxLength={30} value={form.state_registration} onChange={(e) => set('state_registration', e.target.value)} />
                        </div>
                        <div className="space-y-1.5">
                          <FieldLabel>Prazo de pagamento (dias)</FieldLabel>
                          <Input className={inputCls} inputMode="numeric" maxLength={3} value={form.payment_term_days}
                            onChange={(e) => set('payment_term_days', e.target.value.replace(/\D/g, ''))} />
                        </div>
                      </>
                    )}
                  </div>
                </FormSection>
              </>
            )}

            <Separator />

            {/* ================= Observações ================= */}
            <FormSection icon={StickyNote} title="Observações" hint="(opcional)">
              <Textarea className="resize-none border-border/50" rows={3} maxLength={2000} placeholder="Observações..."
                value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </FormSection>
          </div>
        </ScrollArea>

        {/* Rodapé fixo (padrão CustomerFormDialog) */}
        <DialogFooter className="shrink-0 gap-2 border-t p-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {duplicatePerson ? (
            <Button type="button" disabled={addRole.isPending || form.roles.length === 0} onClick={handleAddRolesToExisting}>
              {addRole.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Acrescentar papéis
            </Button>
          ) : (
            <Button type="button" disabled={upsertPerson.isPending} onClick={handleSave}>
              {upsertPerson.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {person ? 'Salvar' : 'Criar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
